import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Pull down to refresh.
 *
 * The data already refreshes on its own, so this is not about correctness — it
 * is about the moment of doubt after paying for something, when you open the
 * app and want to make the number move rather than wonder whether it is
 * current. An app that cannot be asked feels stale even when it is not.
 *
 * The page scrolls on the document rather than in a nested element, so the
 * listeners go on the window and only engage at the very top. touchmove is
 * bound non-passively because the pull has to call preventDefault — without it
 * iOS runs its own rubber-band scroll underneath and the indicator jitters
 * against a moving page.
 */

/** Pull needed to arm the refresh. Roughly a thumb's travel. */
const THRESHOLD = 72;
/** Cap, so a hard drag does not push the content off screen. */
const MAX_PULL = 110;

/**
 * True when the touch began somewhere the page behind must not react to.
 *
 * Two cases, and both matter. An element that scrolls on its own — a long
 * sheet, the horizontal period strip — would be prevented from scrolling,
 * because the pull cancels touchmove. And any modal at all, scrolling or not:
 * while a sheet is open the page underneath is not what the user is touching,
 * so a drag there must never refresh it. Checking only for scrollability missed
 * the second case entirely, since a short sheet does not overflow.
 */
function startedInsideOverlay(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null;
  while (el && el !== document.body) {
    if (el.closest('[role="dialog"], .sheet-backdrop, .app-lock')) return true;
    const style = getComputedStyle(el);
    const scrollsY = /auto|scroll/.test(style.overflowY) && el.scrollHeight > el.clientHeight + 1;
    const scrollsX = /auto|scroll/.test(style.overflowX) && el.scrollWidth > el.clientWidth + 1;
    if (scrollsY || scrollsX) return true;
    el = el.parentElement;
  }
  return false;
}

export function PullToRefresh({ onRefresh, children }: {
  onRefresh: () => void | Promise<unknown>;
  children: React.ReactNode;
}): JSX.Element {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const startY = useRef<number | null>(null);
  const active = useRef(false);
  // Read inside listeners that are bound once; state would be stale there.
  const refreshingRef = useRef(false);
  const pullRef = useRef(0);
  pullRef.current = pull;

  const run = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    setRefreshing(true);
    // Hold the indicator at the threshold while the work happens, so the
    // spinner does not flash for a fast response.
    setPull(THRESHOLD);
    try {
      await onRefresh();
    } finally {
      refreshingRef.current = false;
      setRefreshing(false);
      setPull(0);
    }
  }, [onRefresh]);

  useEffect(() => {
    const onStart = (e: TouchEvent) => {
      // Only from a genuine resting position at the top. Starting mid-scroll
      // would fight the scroller.
      if (
        window.scrollY > 0 ||
        refreshingRef.current ||
        e.touches.length !== 1 ||
        startedInsideOverlay(e.target)
      ) {
        startY.current = null;
        return;
      }
      startY.current = e.touches[0]!.clientY;
      active.current = false;
    };

    const onMove = (e: TouchEvent) => {
      if (startY.current === null || refreshingRef.current) return;
      const dy = e.touches[0]!.clientY - startY.current;

      // An upward drag is an ordinary scroll: let go of it entirely.
      if (dy <= 0) {
        if (active.current) { active.current = false; setPull(0); }
        startY.current = null;
        return;
      }
      if (window.scrollY > 0) { startY.current = null; setPull(0); return; }

      active.current = true;
      // Resistance, so the sheet feels attached to the finger rather than
      // sliding freely, and cannot be flung off screen.
      const resisted = Math.min(MAX_PULL, dy * 0.45);
      setPull(resisted);
      if (e.cancelable) e.preventDefault();
    };

    const onEnd = () => {
      if (startY.current === null) return;
      const armed = active.current && pullRef.current >= THRESHOLD;
      startY.current = null;
      active.current = false;
      if (armed) void run();
      else setPull(0);
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [run]);

  const progress = Math.min(1, pull / THRESHOLD);
  const armed = progress >= 1;

  return (
    <div className="ptr">
      <div
        className="ptr__indicator"
        style={{ height: pull, opacity: pull > 4 ? 1 : 0 }}
        aria-hidden={!refreshing}
        aria-live="polite"
      >
        <span
          className={`ptr__mark${refreshing ? ' ptr__mark--spin' : ''}`}
          style={{
            transform: refreshing ? undefined : `rotate(${progress * 270}deg) scale(${0.6 + progress * 0.4})`,
            borderTopColor: armed || refreshing ? 'var(--accent)' : 'var(--border-strong)',
          }}
        />
      </div>

      <div
        className="ptr__body"
        style={{
          transform: pull ? `translateY(${pull}px)` : undefined,
          // Animate only on release, never while the finger is down — a
          // transition during the drag lags behind the thumb.
          transition: startY.current === null ? 'transform 0.24s cubic-bezier(0.22, 1, 0.36, 1)' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}
